<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { supabase } from '../lib/supabase';
import { createPendingQueue, isNetworkError } from '../lib/pendingQueue';
import MonthlyHistory from './MonthlyHistory.vue';
import StudentCard from './StudentCard.vue';

const props = defineProps({
  user: { type: Object, required: true },
});

const students = ref([]);
const visits = ref([]);
const offDays = ref([]);
const loading = ref(true);
const errorMessage = ref('');

const [pendingVisits, queuePendingVisits, flushPendingVisits] =
  createPendingQueue({
    storageKey: 'pendingVisits',
    insert: (rows) => supabase.from('visits').insert(rows),
    onFlushed: () => fetchData(),
  });
const [pendingOffDays, queuePendingOffDays, flushPendingOffDays] =
  createPendingQueue({
    storageKey: 'pendingOffDays',
    insert: (rows) => supabase.from('off_days').insert(rows),
    onFlushed: () => fetchData(),
  });

async function flushAllPending() {
  await flushPendingVisits();
  await flushPendingOffDays();
}

const addDialogOpen = ref(false);
const newStudentName = ref('');
const addingStudent = ref(false);

const editStudentDialogOpen = ref(false);
const editingStudentId = ref(null);
const editStudentName = ref('');
const savingStudentName = ref(false);

// Absences are logged for the whole day, so the visit's time isn't
// meaningful — this is just a fixed placeholder to store.
const ABSENT_TIME_VALUE = '00:00';

// School day runs 7:30 AM - 2:30 PM, offered in 30-minute blocks; "Custom
// time…" switches the picker to a free-entry time input for anything else.
const SCHOOL_START_MINUTES = 7 * 60 + 30;
const SCHOOL_END_MINUTES = 14 * 60 + 30;
const CUSTOM_TIME_OPTION = 'custom';

const TIME_SLOTS = [];
for (let m = SCHOOL_START_MINUTES; m + 30 <= SCHOOL_END_MINUTES; m += 30) {
  const hh = String(Math.floor(m / 60)).padStart(2, '0');
  const mm = String(m % 60).padStart(2, '0');
  const hour12 = ((Math.floor(m / 60) + 11) % 12) + 1;
  const period = m < 12 * 60 ? 'AM' : 'PM';
  TIME_SLOTS.push({ value: `${hh}:${mm}`, label: `${hour12}:${mm} ${period}` });
}

function isSlotTime(value) {
  return TIME_SLOTS.some((slot) => slot.value === value);
}

const SUBJECTS = [
  'Reading',
  'Math',
  'Social Skills',
  'Behavior',
  'Behavior Goals',
  'Breaks',
  'Intervention',
  'Adapted PE',
  'Rest',
  'Reteaching',
  'Testing',
  'Absent',
];

function todayLocalDate() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function nearestSlotValue() {
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  let closest = TIME_SLOTS[0];
  let smallestDiff = Infinity;
  for (const slot of TIME_SLOTS) {
    const [h, m] = slot.value.split(':').map(Number);
    const diff = Math.abs(h * 60 + m - nowMinutes);
    if (diff < smallestDiff) {
      smallestDiff = diff;
      closest = slot;
    }
  }
  return closest.value;
}

function localDateAndTime(isoString) {
  const d = new Date(isoString);
  const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return { date, time };
}

const logVisitDialogOpen = ref(false);
const loggingVisit = ref(false);
const visitStudentIds = ref([]);
const visitNotes = ref('');
const visitDate = ref(todayLocalDate());
const visitTime = ref(nearestSlotValue());
const visitTimeIsCustom = ref(false);
const visitDuration = ref(30);
const visitSubject = ref('');

function onVisitTimeSelect(value) {
  if (value === CUSTOM_TIME_OPTION) {
    visitTimeIsCustom.value = true;
  } else {
    visitTime.value = value;
  }
}

const OFF_DAY_TYPES = [
  { value: 'holiday', label: 'School holiday' },
  { value: 'out', label: 'Out' },
];

const logOffDayDialogOpen = ref(false);
const loggingOffDay = ref(false);
const offDayDate = ref(todayLocalDate());
const offDayType = ref('holiday');
const offDayReason = ref('');

const editOffDayDialogOpen = ref(false);
const savingOffDay = ref(false);
const editingOffDayId = ref(null);
const editOffDayDate = ref('');
const editOffDayType = ref('holiday');
const editOffDayReason = ref('');

const editDialogOpen = ref(false);
const savingEdit = ref(false);
// The original {studentId, visitId} rows making up the group being edited,
// so saving can diff against the (possibly changed) student selection —
// updating rows that stayed, deleting ones removed, inserting new ones.
const editOriginalEntries = ref([]);
const editStudentIds = ref([]);
const editNotes = ref('');
const editDate = ref('');
const editTime = ref('');
const editTimeIsCustom = ref(false);
const editDuration = ref(30);
const editSubject = ref('');

function onEditTimeSelect(value) {
  if (value === CUSTOM_TIME_OPTION) {
    editTimeIsCustom.value = true;
  } else {
    editTime.value = value;
  }
}

const visitsByStudent = computed(() => {
  const map = {};
  for (const student of students.value) map[student.id] = [];
  for (const visit of visits.value) {
    map[visit.student_id]?.push(visit);
  }
  return map;
});

async function fetchData() {
  // Toggling `loading` unmounts/remounts wa-tab-group (see the v-else in the
  // template), which would otherwise reset the active tab back to its
  // default ("students") every time a mutation refetches.
  const activeTab = tabGroupEl.value?.active;
  loading.value = true;
  errorMessage.value = '';

  const [studentsResult, visitsResult, offDaysResult] = await Promise.all([
    supabase.from('students').select('*').order('name'),
    supabase.from('visits').select('*').order('seen_at', { ascending: false }),
    supabase.from('off_days').select('*').order('date', { ascending: false }),
  ]);

  if (studentsResult.error || visitsResult.error || offDaysResult.error) {
    errorMessage.value = (
      studentsResult.error ||
      visitsResult.error ||
      offDaysResult.error
    ).message;
  } else {
    students.value = studentsResult.data;
    visits.value = visitsResult.data;
    offDays.value = offDaysResult.data;
  }

  loading.value = false;
  if (activeTab) {
    await nextTick();
    if (tabGroupEl.value) {
      tabGroupEl.value.active = activeTab;
    }
  }
}

async function addStudent() {
  const name = newStudentName.value.trim();
  if (!name) {
    errorMessage.value = 'Enter a name before adding a student.';
    return;
  }

  addingStudent.value = true;
  const { error } = await supabase
    .from('students')
    .insert({ name, user_id: props.user.id });
  addingStudent.value = false;

  if (error) {
    errorMessage.value = error.message;
    return;
  }

  newStudentName.value = '';
  addDialogOpen.value = false;
  await fetchData();
}

async function removeStudent(studentId) {
  const student = students.value.find((s) => s.id === studentId);
  if (
    !window.confirm(
      `Remove ${student?.name} and all of their visit history? This can't be undone.`,
    )
  ) {
    return;
  }

  const { error } = await supabase
    .from('students')
    .delete()
    .eq('id', studentId);
  if (error) {
    errorMessage.value = error.message;
    return;
  }

  await fetchData();
}

function openEditStudentDialog(student) {
  editingStudentId.value = student.id;
  editStudentName.value = student.name;
  errorMessage.value = '';
  editStudentDialogOpen.value = true;
}

async function saveStudentName() {
  const name = editStudentName.value.trim();
  if (!name) {
    errorMessage.value = 'Enter a name before saving.';
    return;
  }

  savingStudentName.value = true;
  const { error } = await supabase
    .from('students')
    .update({ name })
    .eq('id', editingStudentId.value);
  savingStudentName.value = false;

  if (error) {
    errorMessage.value = error.message;
    return;
  }

  editStudentDialogOpen.value = false;
  await fetchData();
}

async function deleteVisit(visitId, studentName) {
  if (
    !window.confirm(
      `Remove ${studentName} from this visit? This can't be undone.`,
    )
  ) {
    return;
  }

  const { error } = await supabase.from('visits').delete().eq('id', visitId);
  if (error) {
    errorMessage.value = error.message;
    return;
  }

  await fetchData();
}

// A "group visit" is just several rows sharing the same seen_at/subject/
// duration (that's how logVisit creates them, and how MonthlyHistory groups
// them for display). Editing any one row's visit re-derives the whole group
// from the current visits list, so the dialog edits all of them together.
function openEditVisitDialog(visit) {
  const siblings = visits.value.filter(
    (v) =>
      v.seen_at === visit.seen_at &&
      v.subject === visit.subject &&
      v.duration_minutes === visit.duration_minutes,
  );
  const { date, time } = localDateAndTime(visit.seen_at);
  editOriginalEntries.value = siblings.map((v) => ({
    studentId: v.student_id,
    visitId: v.id,
  }));
  editStudentIds.value = siblings.map((v) => v.student_id);
  editNotes.value = visit.notes ?? '';
  editDate.value = date;
  editTime.value = time;
  editTimeIsCustom.value = !isSlotTime(time);
  editDuration.value = visit.duration_minutes ?? 30;
  editSubject.value = visit.subject ?? '';
  errorMessage.value = '';
  editDialogOpen.value = true;
}

async function saveEditedVisit() {
  if (!editStudentIds.value.length) {
    errorMessage.value = 'Choose at least one student before saving.';
    return;
  }
  if (!editSubject.value) {
    errorMessage.value = 'Choose a subject before saving.';
    return;
  }

  savingEdit.value = true;
  const subject = editSubject.value;
  const time = subject === 'Absent' ? ABSENT_TIME_VALUE : editTime.value;
  const seenAt = new Date(`${editDate.value}T${time}:00`).toISOString();
  const notes = editNotes.value.trim() || null;
  const durationMinutes = subject === 'Absent' ? null : editDuration.value;

  const originalStudentIds = new Set(
    editOriginalEntries.value.map((e) => e.studentId),
  );
  const newStudentIds = new Set(editStudentIds.value);

  const idsToDelete = editOriginalEntries.value
    .filter((e) => !newStudentIds.has(e.studentId))
    .map((e) => e.visitId);
  const idsToUpdate = editOriginalEntries.value
    .filter((e) => newStudentIds.has(e.studentId))
    .map((e) => e.visitId);
  const studentIdsToInsert = editStudentIds.value.filter(
    (id) => !originalStudentIds.has(id),
  );

  const tasks = [];
  if (idsToDelete.length) {
    tasks.push(supabase.from('visits').delete().in('id', idsToDelete));
  }
  if (idsToUpdate.length) {
    tasks.push(
      supabase
        .from('visits')
        .update({
          notes,
          seen_at: seenAt,
          duration_minutes: durationMinutes,
          subject,
        })
        .in('id', idsToUpdate),
    );
  }
  if (studentIdsToInsert.length) {
    tasks.push(
      supabase.from('visits').insert(
        studentIdsToInsert.map((studentId) => ({
          student_id: studentId,
          notes,
          seen_at: seenAt,
          duration_minutes: durationMinutes,
          subject,
          user_id: props.user.id,
        })),
      ),
    );
  }

  const results = await Promise.all(tasks);
  savingEdit.value = false;

  const failed = results.find((result) => result.error);
  if (failed) {
    errorMessage.value = failed.error.message;
    return;
  }

  editDialogOpen.value = false;
  await fetchData();
}

// wa-select re-emits wa-after-hide when its own dropdown closes, and it
// bubbles up into ancestor wa-dialogs — ignore hides that didn't originate
// from the dialog itself so picking a student/time doesn't close the dialog.
function onDialogHide(event, onClose) {
  if (event.target === event.currentTarget) onClose();
}

function openLogVisitDialog() {
  visitStudentIds.value = [];
  visitNotes.value = '';
  visitDate.value = todayLocalDate();
  visitTime.value = nearestSlotValue();
  visitTimeIsCustom.value = false;
  visitDuration.value = 30;
  visitSubject.value = '';
  errorMessage.value = '';
  logVisitDialogOpen.value = true;
}

async function logVisit() {
  if (!visitStudentIds.value.length) {
    errorMessage.value = 'Choose at least one student before logging a visit.';
    return;
  }
  if (!visitSubject.value) {
    errorMessage.value = 'Choose a subject before logging a visit.';
    return;
  }

  const time =
    visitSubject.value === 'Absent' ? ABSENT_TIME_VALUE : visitTime.value;
  const seenAt = new Date(`${visitDate.value}T${time}:00`).toISOString();

  const duplicateNames = visitStudentIds.value
    .filter((studentId) =>
      visits.value.some(
        (v) =>
          v.student_id === studentId &&
          new Date(v.seen_at).getTime() === new Date(seenAt).getTime() &&
          v.subject === visitSubject.value,
      ),
    )
    .map((studentId) => students.value.find((s) => s.id === studentId)?.name)
    .filter(Boolean);

  if (
    duplicateNames.length &&
    !window.confirm(
      `${duplicateNames.join(', ')} already ${duplicateNames.length === 1 ? 'has' : 'have'} a "${visitSubject.value}" visit logged at this date and time. Log it again?`,
    )
  ) {
    return;
  }

  loggingVisit.value = true;
  const notes = visitNotes.value.trim() || null;
  const payloads = visitStudentIds.value.map((studentId) => ({
    student_id: studentId,
    notes,
    seen_at: seenAt,
    duration_minutes:
      visitSubject.value === 'Absent' ? null : visitDuration.value,
    subject: visitSubject.value,
    user_id: props.user.id,
  }));

  if (!navigator.onLine) {
    queuePendingVisits(payloads);
    loggingVisit.value = false;
    logVisitDialogOpen.value = false;
    return;
  }

  const { error } = await supabase.from('visits').insert(payloads);
  loggingVisit.value = false;

  if (error) {
    if (isNetworkError(error)) {
      queuePendingVisits(payloads);
      logVisitDialogOpen.value = false;
      return;
    }
    errorMessage.value = error.message;
    return;
  }

  logVisitDialogOpen.value = false;
  await fetchData();
}

function openLogOffDayDialog() {
  offDayDate.value = todayLocalDate();
  offDayType.value = 'holiday';
  offDayReason.value = '';
  errorMessage.value = '';
  logOffDayDialogOpen.value = true;
}

async function logOffDay() {
  loggingOffDay.value = true;
  const payload = {
    date: offDayDate.value,
    reason_type: offDayType.value,
    note: offDayReason.value.trim() || null,
    user_id: props.user.id,
  };

  if (!navigator.onLine) {
    queuePendingOffDays([payload]);
    loggingOffDay.value = false;
    logOffDayDialogOpen.value = false;
    return;
  }

  const { error } = await supabase.from('off_days').insert(payload);
  loggingOffDay.value = false;

  if (error) {
    if (isNetworkError(error)) {
      queuePendingOffDays([payload]);
      logOffDayDialogOpen.value = false;
      return;
    }
    errorMessage.value = error.message;
    return;
  }

  logOffDayDialogOpen.value = false;
  await fetchData();
}

function openEditOffDayDialog(offDay) {
  editingOffDayId.value = offDay.id;
  editOffDayDate.value = offDay.date;
  editOffDayType.value = offDay.reason_type;
  editOffDayReason.value = offDay.note ?? '';
  errorMessage.value = '';
  editOffDayDialogOpen.value = true;
}

async function saveEditedOffDay() {
  savingOffDay.value = true;
  const { error } = await supabase
    .from('off_days')
    .update({
      date: editOffDayDate.value,
      reason_type: editOffDayType.value,
      note: editOffDayReason.value.trim() || null,
    })
    .eq('id', editingOffDayId.value);
  savingOffDay.value = false;

  if (error) {
    errorMessage.value = error.message;
    return;
  }

  editOffDayDialogOpen.value = false;
  await fetchData();
}

async function deleteOffDay(offDayId) {
  if (!window.confirm("Remove this day off entry? This can't be undone.")) {
    return;
  }

  const { error } = await supabase.from('off_days').delete().eq('id', offDayId);
  if (error) {
    errorMessage.value = error.message;
    return;
  }

  await fetchData();
}

function signOut() {
  if (!window.confirm('Sign out?')) {
    return;
  }
  supabase.auth.signOut();
}

const tabGroupEl = ref(null);

async function printMonthlyLog() {
  if (tabGroupEl.value) tabGroupEl.value.active = 'monthly';
  await nextTick();
  window.print();
}

let pendingSyncInterval;

onMounted(async () => {
  await fetchData();
  await flushAllPending();
  window.addEventListener('online', flushAllPending);
  // Belt-and-suspenders for connections that come back flaky rather than
  // cleanly, where the browser's 'online' event may not fire reliably.
  pendingSyncInterval = setInterval(flushAllPending, 30000);
});

onUnmounted(() => {
  window.removeEventListener('online', flushAllPending);
  clearInterval(pendingSyncInterval);
});
</script>

<template>
  <div class="page">
    <header class="page-header">
      <h1>Student Sessions</h1>
      <wa-button appearance="outlined" @click="signOut">Sign out</wa-button>
    </header>

    <wa-callout v-if="errorMessage" variant="danger">{{
      errorMessage
    }}</wa-callout>

    <wa-callout v-if="pendingVisits.length" variant="warning" class="no-print">
      {{ pendingVisits.length }}
      {{ pendingVisits.length === 1 ? 'visit' : 'visits' }} saved offline — will
      sync automatically once you're back online.
    </wa-callout>

    <wa-callout v-if="pendingOffDays.length" variant="warning" class="no-print">
      {{ pendingOffDays.length }}
      {{ pendingOffDays.length === 1 ? 'day off' : 'days off' }} saved offline —
      will sync automatically once you're back online.
    </wa-callout>

    <div class="toolbar">
      <wa-button
        appearance="outlined"
        variant="brand"
        :disabled="!students.length"
        @click="openLogVisitDialog"
      >
        Log visit
      </wa-button>
      <wa-button appearance="outlined" @click="openLogOffDayDialog"
        >Log day off</wa-button
      >
      <wa-button
        variant="brand"
        @click="
          errorMessage = '';
          addDialogOpen = true;
        "
        >Add student</wa-button
      >
    </div>

    <div v-if="loading" class="loading">
      <wa-spinner></wa-spinner>
    </div>

    <wa-tab-group v-else ref="tabGroupEl">
      <wa-tab panel="students">Students</wa-tab>
      <wa-tab panel="monthly">Monthly view</wa-tab>

      <wa-tab-panel name="students" active>
        <p v-if="!students.length" class="empty">
          No students yet — add one to get started.
        </p>
        <div v-else class="student-list">
          <StudentCard
            v-for="student in students"
            :key="student.id"
            :student="student"
            :visits="visitsByStudent[student.id]"
            :remove-student="removeStudent"
            :edit-student="openEditStudentDialog"
            :delete-visit="deleteVisit"
            :edit-visit="openEditVisitDialog"
          />
        </div>
      </wa-tab-panel>

      <wa-tab-panel name="monthly">
        <MonthlyHistory
          :visits="visits"
          :students="students"
          :off-days="offDays"
          :delete-visit="deleteVisit"
          :edit-visit="openEditVisitDialog"
          :edit-off-day="openEditOffDayDialog"
          :delete-off-day="deleteOffDay"
          :print-visit-log="printMonthlyLog"
        />
      </wa-tab-panel>
    </wa-tab-group>
  </div>

  <wa-dialog
    :open="addDialogOpen"
    label="Add student"
    @wa-after-hide="
      onDialogHide($event, () => {
        addDialogOpen = false;
        newStudentName = '';
        errorMessage = '';
      })
    "
  >
    <wa-callout v-if="errorMessage" variant="danger" class="dialog-error">{{
      errorMessage
    }}</wa-callout>
    <wa-input
      :value="newStudentName"
      label="Student name"
      autofocus
      @input="newStudentName = $event.target.value"
      @keydown.enter="addStudent"
    ></wa-input>
    <wa-button slot="footer" appearance="plain" @click="addDialogOpen = false"
      >Cancel</wa-button
    >
    <wa-button
      slot="footer"
      variant="brand"
      :loading="addingStudent"
      @click="addStudent"
      >Add</wa-button
    >
  </wa-dialog>

  <wa-dialog
    :open="editStudentDialogOpen"
    label="Edit student"
    @wa-after-hide="
      onDialogHide($event, () => {
        editStudentDialogOpen = false;
        errorMessage = '';
      })
    "
  >
    <wa-callout v-if="errorMessage" variant="danger" class="dialog-error">{{
      errorMessage
    }}</wa-callout>
    <wa-input
      :value="editStudentName"
      label="Student name"
      autofocus
      @input="editStudentName = $event.target.value"
      @keydown.enter="saveStudentName"
    ></wa-input>
    <wa-button
      slot="footer"
      appearance="plain"
      @click="editStudentDialogOpen = false"
      >Cancel</wa-button
    >
    <wa-button
      slot="footer"
      variant="brand"
      :loading="savingStudentName"
      @click="saveStudentName"
      >Save</wa-button
    >
  </wa-dialog>

  <wa-dialog
    :open="logVisitDialogOpen"
    label="Log visit"
    @wa-after-hide="
      onDialogHide($event, () => {
        logVisitDialogOpen = false;
        errorMessage = '';
      })
    "
  >
    <wa-callout v-if="errorMessage" variant="danger" class="dialog-error">{{
      errorMessage
    }}</wa-callout>
    <wa-select
      :value="visitStudentIds"
      label="Students"
      placeholder="Choose one or more students"
      multiple
      @input="visitStudentIds = $event.target.value"
    >
      <wa-option
        v-for="student in students"
        :key="student.id"
        :value="student.id"
      >
        {{ student.name }}
      </wa-option>
    </wa-select>
    <div class="visit-subject-duration">
      <wa-select
        :value="visitSubject"
        label="Subject"
        placeholder="Choose a subject"
        @input="visitSubject = $event.target.value"
      >
        <wa-option
          v-for="subject in SUBJECTS"
          :key="subject"
          :value="subject"
          >{{ subject }}</wa-option
        >
      </wa-select>
      <wa-radio-group
        v-if="visitSubject !== 'Absent'"
        :value="String(visitDuration)"
        label="Duration"
        @input="visitDuration = Number($event.target.value)"
      >
        <wa-radio value="10">10 min</wa-radio>
        <wa-radio value="15">15 min</wa-radio>
        <wa-radio value="20">20 min</wa-radio>
        <wa-radio value="30">30 min</wa-radio>
        <wa-radio value="45">45 min</wa-radio>
      </wa-radio-group>
    </div>
    <div class="visit-datetime">
      <wa-input
        type="date"
        :value="visitDate"
        label="Date"
        @input="visitDate = $event.target.value"
      ></wa-input>
      <wa-select
        v-if="visitSubject !== 'Absent' && !visitTimeIsCustom"
        :value="visitTime"
        label="Time"
        @input="onVisitTimeSelect($event.target.value)"
      >
        <wa-option
          v-for="slot in TIME_SLOTS"
          :key="slot.value"
          :value="slot.value"
        >
          {{ slot.label }}
        </wa-option>
        <wa-option :value="CUSTOM_TIME_OPTION">Custom time…</wa-option>
      </wa-select>
      <div
        v-if="visitSubject !== 'Absent' && visitTimeIsCustom"
        class="custom-time"
      >
        <wa-input
          type="time"
          :value="visitTime"
          label="Time"
          @input="visitTime = $event.target.value"
        ></wa-input>
        <wa-button
          size="s"
          appearance="neutral"
          @click="visitTimeIsCustom = false"
          >Use preset times</wa-button
        >
      </div>
    </div>
    <wa-textarea
      :value="visitNotes"
      label="Notes (optional)"
      rows="3"
      @input="visitNotes = $event.target.value"
    ></wa-textarea>
    <wa-button
      slot="footer"
      appearance="plain"
      @click="logVisitDialogOpen = false"
      >Cancel</wa-button
    >
    <wa-button
      slot="footer"
      variant="brand"
      :loading="loggingVisit"
      @click="logVisit"
      >Save</wa-button
    >
  </wa-dialog>

  <wa-dialog
    :open="editDialogOpen"
    label="Edit visit"
    @wa-after-hide="
      onDialogHide($event, () => {
        editDialogOpen = false;
        errorMessage = '';
      })
    "
  >
    <wa-callout v-if="errorMessage" variant="danger" class="dialog-error">{{
      errorMessage
    }}</wa-callout>
    <wa-select
      :value="editStudentIds"
      label="Students"
      placeholder="Choose one or more students"
      multiple
      @input="editStudentIds = $event.target.value"
    >
      <wa-option
        v-for="student in students"
        :key="student.id"
        :value="student.id"
      >
        {{ student.name }}
      </wa-option>
    </wa-select>
    <div class="visit-subject-duration">
      <wa-select
        :value="editSubject"
        label="Subject"
        placeholder="Choose a subject"
        @input="editSubject = $event.target.value"
      >
        <wa-option
          v-for="subject in SUBJECTS"
          :key="subject"
          :value="subject"
          >{{ subject }}</wa-option
        >
      </wa-select>
      <wa-radio-group
        v-if="editSubject !== 'Absent'"
        :value="String(editDuration)"
        label="Duration"
        @input="editDuration = Number($event.target.value)"
      >
        <wa-radio value="10">10 min</wa-radio>
        <wa-radio value="15">15 min</wa-radio>
        <wa-radio value="20">20 min</wa-radio>
        <wa-radio value="30">30 min</wa-radio>
        <wa-radio value="45">45 min</wa-radio>
      </wa-radio-group>
    </div>
    <div class="visit-datetime">
      <wa-input
        type="date"
        :value="editDate"
        label="Date"
        @input="editDate = $event.target.value"
      ></wa-input>
      <wa-select
        v-if="editSubject !== 'Absent' && !editTimeIsCustom"
        :value="editTime"
        label="Time"
        @input="onEditTimeSelect($event.target.value)"
      >
        <wa-option
          v-for="slot in TIME_SLOTS"
          :key="slot.value"
          :value="slot.value"
        >
          {{ slot.label }}
        </wa-option>
        <wa-option :value="CUSTOM_TIME_OPTION">Custom time…</wa-option>
      </wa-select>
      <div
        v-if="editSubject !== 'Absent' && editTimeIsCustom"
        class="custom-time"
      >
        <wa-input
          type="time"
          :value="editTime"
          label="Time"
          @input="editTime = $event.target.value"
        ></wa-input>
        <wa-button
          size="s"
          appearance="neutral"
          @click="editTimeIsCustom = false"
          >Use preset times</wa-button
        >
      </div>
    </div>
    <wa-textarea
      :value="editNotes"
      label="Notes (optional)"
      rows="3"
      @input="editNotes = $event.target.value"
    ></wa-textarea>
    <wa-button slot="footer" appearance="plain" @click="editDialogOpen = false"
      >Cancel</wa-button
    >
    <wa-button
      slot="footer"
      variant="brand"
      :loading="savingEdit"
      @click="saveEditedVisit"
      >Save</wa-button
    >
  </wa-dialog>

  <wa-dialog
    :open="logOffDayDialogOpen"
    label="Log day off"
    @wa-after-hide="
      onDialogHide($event, () => {
        logOffDayDialogOpen = false;
        errorMessage = '';
      })
    "
  >
    <wa-callout v-if="errorMessage" variant="danger" class="dialog-error">{{
      errorMessage
    }}</wa-callout>
    <wa-input
      type="date"
      :value="offDayDate"
      label="Date"
      @input="offDayDate = $event.target.value"
    ></wa-input>
    <wa-radio-group
      :value="offDayType"
      label="Reason"
      class="off-day-type"
      @input="offDayType = $event.target.value"
    >
      <wa-radio
        v-for="type in OFF_DAY_TYPES"
        :key="type.value"
        :value="type.value"
        >{{ type.label }}</wa-radio
      >
    </wa-radio-group>
    <wa-textarea
      :value="offDayReason"
      label="Details (optional)"
      rows="2"
      @input="offDayReason = $event.target.value"
    ></wa-textarea>
    <wa-button
      slot="footer"
      appearance="plain"
      @click="logOffDayDialogOpen = false"
      >Cancel</wa-button
    >
    <wa-button
      slot="footer"
      variant="brand"
      :loading="loggingOffDay"
      @click="logOffDay"
      >Save</wa-button
    >
  </wa-dialog>

  <wa-dialog
    :open="editOffDayDialogOpen"
    label="Edit day off"
    @wa-after-hide="
      onDialogHide($event, () => {
        editOffDayDialogOpen = false;
        errorMessage = '';
      })
    "
  >
    <wa-callout v-if="errorMessage" variant="danger" class="dialog-error">{{
      errorMessage
    }}</wa-callout>
    <wa-input
      type="date"
      :value="editOffDayDate"
      label="Date"
      @input="editOffDayDate = $event.target.value"
    ></wa-input>
    <wa-radio-group
      :value="editOffDayType"
      label="Reason"
      class="off-day-type"
      @input="editOffDayType = $event.target.value"
    >
      <wa-radio
        v-for="type in OFF_DAY_TYPES"
        :key="type.value"
        :value="type.value"
        >{{ type.label }}</wa-radio
      >
    </wa-radio-group>
    <wa-textarea
      :value="editOffDayReason"
      label="Details (optional)"
      rows="2"
      @input="editOffDayReason = $event.target.value"
    ></wa-textarea>
    <wa-button
      slot="footer"
      appearance="plain"
      @click="editOffDayDialogOpen = false"
      >Cancel</wa-button
    >
    <wa-button
      slot="footer"
      variant="brand"
      :loading="savingOffDay"
      @click="saveEditedOffDay"
      >Save</wa-button
    >
  </wa-dialog>
</template>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  gap: var(--wa-space-l);
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-header h1 {
  font-size: var(--wa-font-size-l);
  margin: 0;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--wa-space-s);
}

.dialog-error {
  margin-bottom: var(--wa-space-m);
}

.visit-datetime {
  display: flex;
  gap: var(--wa-space-s);
  margin-block: var(--wa-space-m);
}

.visit-datetime > * {
  flex: 1;
}

.custom-time {
  display: flex;
  align-items: flex-end;
  gap: var(--wa-space-2xs);
}

.custom-time wa-input {
  flex: 1;
}

.visit-subject-duration {
  display: flex;
  align-items: flex-end;
  gap: var(--wa-space-l);
  margin-bottom: var(--wa-space-m);
}

.visit-subject-duration wa-select {
  flex: 1;
}

.off-day-type {
  margin-block: var(--wa-space-m);
}

.loading {
  display: flex;
  justify-content: center;
  padding: var(--wa-space-2xl) 0;
  font-size: 2rem;
}

.empty {
  color: var(--wa-color-text-quiet);
  text-align: center;
}

.student-list {
  display: flex;
  flex-direction: column;
  gap: var(--wa-space-m);
}

@media (max-width: 640px) {
  .visit-datetime {
    flex-direction: column;
    align-items: stretch;
  }
}

/* Printing (via "Print visit log") switches to the monthly tab and hides
   everything but that tab's content, so the printed page is just the log. */
@media print {
  .page-header,
  .toolbar,
  .no-print,
  wa-dialog {
    display: none !important;
  }

  wa-tab-group::part(nav) {
    display: none;
  }

  wa-tab-group::part(body) {
    padding: 0;
  }
}
</style>
